import { useCallback, useEffect, useMemo, useState } from "react";
import { getEdificiosOperativosPublico } from "./getEdificiosPublico";
import { calcularResumenRedComercial } from "./getResumenRedComercial";

// Países soportados por el universo comercial (10.10). "Region" (hostname
// adigitalholding.com, ver auth.js getPaisFromHostname) usa Chile, mismo default
// que el resto del proyecto. Exportada para que fetches hermanos del mismo país
// apliquen el mismo guard, sin duplicarlo — hoy sin consumidores externos (Automotriz
// dejó de fetchear `ipsos_categorias` en la Fase 3, ver docs/modulos/categorias-analisis-mercado.md).
export const paisParaFetch = (pais) => {
  if (pais === "Chile" || pais === "Perú") return pais;
  if (pais === "Region") return "Chile";
  return null;
};

// Fuente única del universo (edificios, pantallas, personas, empresas) para las 15
// páginas de /categorias/* — mismo cálculo que el mediakit v2 (10.1): nunca suma
// propia, siempre calcularResumenRedComercial/contarEmpresasEdificio.
export const useUniversoCategorias = (pais) => {
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});

  const paisFetch = paisParaFetch(pais);

  useEffect(() => {
    // pais null/vacío (auth.js todavía no lo seteó): no dispara fetch, se queda
    // cargando — evita la primera carga sin país (10.177: nunca fetch con filtro vacío).
    if (!paisFetch) return;

    let cancelado = false;
    setLoading(true);
    setError(null);

    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais: paisFetch });
        if (cancelado) return;

        if (newEdificios.length === 0) {
          // 10.177/10.273: error explícito, nunca Loader infinito.
          setError(`No se encontraron edificios operativos para ${paisFetch}.`);
          setEdificios([]);
          setCheckedBuildings({});
          return;
        }

        setEdificios(newEdificios);
        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
        setCheckAll(true);
      } catch (err) {
        if (!cancelado) setError(err.message || "Error cargando el universo de edificios.");
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    initialize();
    return () => {
      cancelado = true;
    };
  }, [paisFetch]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const { cantidadEdificios, totalPantallas, totalEjecutivos, totalEmpresas } = useMemo(() => {
    const edificiosSeleccionados = edificios.filter(({ id }) => checkedBuildings[id]);
    const resumen = calcularResumenRedComercial(edificiosSeleccionados);

    return {
      cantidadEdificios: resumen.totalOperativos,
      totalPantallas: resumen.pantallasOperativos,
      totalEjecutivos: resumen.personasOperativos,
      totalEmpresas: resumen.empresasOperativos,
    };
  }, [edificios, checkedBuildings]);

  return {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  };
};
